'use client';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import { getCategoryWithProducts } from '../api/get-category-with-products';
import type { ICategoryWithProducts } from '../types';

import { CATEGORIES_PER_PAGE } from '../constants';

export interface UseCategoryWithProductsParams {
  menuId: string;
  shipperId: string;
  enabled?: boolean;
  tag_ids?: string;
  sort?: string;
  deliveryType?: string;
  initialData?: ICategoryWithProducts[];
}

export function useCategoryWithProducts({
  menuId,
  shipperId,
  enabled = true,
  tag_ids,
  sort,
  deliveryType = '',
  initialData,
}: UseCategoryWithProductsParams) {
  const normalizedMenuId = menuId && menuId !== 'null' ? menuId : undefined;

  // SSR prefetch has no menu_id — only seed it before branch menu is known.
  const shouldUseInitialData =
    Boolean(initialData?.length) &&
    !tag_ids &&
    !sort &&
    !normalizedMenuId;

  return useQuery({
    queryKey: [
      'category-with-products',
      shipperId,
      normalizedMenuId ?? '',
      tag_ids,
      sort,
      deliveryType,
    ],
    queryFn: async () => {
      const response = await getCategoryWithProducts({
        page: 1,
        limit: CATEGORIES_PER_PAGE,
        is_active: true,
        has_product: true,
        delivery_type: deliveryType || undefined,
        fields: 'discounts,tags',
        shipper_id: shipperId,
        menu_id: normalizedMenuId,
        tag_ids,
        sort,
      });

      return response.categories || [];
    },
    enabled: enabled && Boolean(shipperId),
    initialData: shouldUseInitialData ? initialData : undefined,
    placeholderData: (previousData) => previousData,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/** Branch/session menu id — same rules as brand search products. */
export function useBrandMenuContext(shipperId: string) {
  const { menu_id, is_hall, order_type } = useSelector(
    (state: IRedux) => state.session,
  );
  const branch = useSelector(
    (state: IRedux) => state.common.branches?.[shipperId],
  );

  const effectiveMenuId =
    is_hall === 'true'
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || '';

  return {
    effectiveMenuId,
    deliveryType: order_type?.type || '',
  };
}
